'use strict';

const { pacientes } = require('../../strings/comum');
const Util = require('../../util/util');
const moment = require('moment');
const criteriosEscolhaPaciente = ['nome', 'cpf', 'cartao_sus', 'nome_mae', 'data_nascimento', 'nome_pai', 'id_sexo', 'id_raca', 'id_endereco_paciente', 'id_contato_paciente', 'id_endereco_paciente2'];

const TELEFONE_RESIDENCIAL = 1;
const TELEFONE_CELULAR = 2;
const EMAIL = 4;

const Paciente = {
    pesquisarCidade: async (trx, codigo) => {
        let consulta = trx('public.cidade AS c').join('public.estado AS e', 'e.id', '=', 'c.id_estado').join('public.pais AS p', 'p.id', '=', 'e.id_pais').where('c.codigo_ibge', 'like', `${codigo}%`);

        return await consulta
            .select('c.id AS id_cidade', 'c.nome AS cidade', 'e.id AS id_estado', 'e.nome AS estado', 'e.sigla AS sigla_estado', 'p.id AS id_pais', 'p.nome AS pais', 'p.sigla AS sigla_pais')
            .first();
    },

    pesquisarPais: async (trx, nome) => {
        let consulta = trx('public.pais AS p').where('p.nome', 'ilike', `%${nome}%`);
        return await consulta.select('p.id', 'p.nome').first();
    },

    pesquisarEstado: async (trx, sigla) => {
        let consulta = trx('public.estado AS e').where('e.sigla', 'ilike', sigla);
        return await consulta.select('e.id', 'e.nome').first();
    },

    pesquisarSexo: async (trx, sigla_sexo) => {
        return await trx('public.sexo as s').select('s.*').where('s.sigla', '=', sigla_sexo).first();
    },

    pesquisarRaca: async (trx, raca) => {
        return await trx('public.raca as r').select('r.*').where('r.descricao', '=', raca).first();
    },

    removerContatos: async (trx, idPaciente) => {
        await trx('public.contato_paciente').where('id_paciente', '=', idPaciente).del().transacting(trx);
    },

    /**
     * @param {*} trx
     * @param {*} contato => { id_paciente, id_tipo_contato, contato }
     */
    salvarContato: async (trx, contato) => {
        await trx.insert(contato).into('public.contato_paciente').transacting(trx);
    },

    /**
     *
     * @param {*} trx
     * @param {*} idPaciente
     * @param {*} endereco => { logradouro, bairro, cep, numero, codigoIbge, estado, pais }
     * @returns idEndereco
     */
    salvarEndereco: async (trx, idPaciente, endereco) => {
        let id_cidade, id_estado, id_pais;

        if (endereco.codigoIbge) {
            const cidadeBD = await Paciente.pesquisarCidade(trx, endereco.codigoIbge);
            if (cidadeBD) {
                id_cidade = cidadeBD.id_cidade;
                id_pais = cidadeBD.id_pais;
                id_estado = cidadeBD.id_estado;
            }
        }
        if (!id_pais && endereco?.pais) {
            const pais = await Paciente.pesquisarPais(trx, endereco.pais);
            id_pais = pais?.id || null;
        }
        if (!id_estado && endereco?.estado) {
            const estado = await Paciente.pesquisarEstado(trx, endereco.estado);
            id_estado = estado?.id || null;
        }
        const enderecoBD = {
            id_paciente: idPaciente,
            logradouro: endereco.logradouro || null,
            bairro: endereco.bairro || null,
            cep: endereco.cep || null,
            numero: endereco.numero || null,
            id_cidade: id_cidade || null,
            id_estado: id_estado || null,
            id_pais: id_pais || null
        };

        const result = await trx.insert(enderecoBD).into('public.endereco_paciente').returning('id').transacting(trx);

        return Number.isInteger(result[0]) ? result[0] : result[0].id;
    },

    atualizarEndereco: async (trx, idEndereco, endereco) => {
        const dados = {
            id_paciente: endereco.idPaciente,
            logradouro: endereco.logradouro,
            bairro: endereco.bairro
        };
        if (endereco.cep) {
            dados.cep = endereco.cep;
        }
        if (endereco.municipio) {
            dados.id_cidade = endereco.municipio.id;
        }
        if (endereco.uf) {
            dados.id_estado = endereco.uf.id;
        }
        if (endereco.pais) {
            dados.id_pais = endereco.pais.id;
        }
        if (endereco.numero) {
            dados.numero = endereco.numero;
        }
        const result = await trx('public.endereco_paciente').where('id', '=', idEndereco).update(dados).returning('id').transacting(trx);

        return Number.isInteger(result[0]) ? result[0] : result[0].id;
    },

    /**
     * Salvar um paciente no banco de dados a partir de um objeto pacienteCadsus.
     *
     * @param {*} trx
     * @param {*} pacienteCadsus => {nome, razaoSocial, sexoSigla, dataNascimento, cpf, cns,
     * nomePai, nomeMae, telefone, email, endereco: { cep, logradouro, numero, bairro, codigoIbge, estado, complemento }}
     */
    salvarPacienteCadsus: async (trx, pacienteCadsus) => {
        let id_sexo, id_raca, id_paciente;
        if (pacienteCadsus.sexoSigla) {
            const sexo = await Paciente.pesquisarSexo(trx, pacienteCadsus.sexoSigla);
            id_sexo = sexo?.id || null;
        }
        if (pacienteCadsus.raca) {
            const raca = await Paciente.pesquisarRaca(trx, pacienteCadsus.raca);
            id_raca = raca?.id || null;
        }

        // Montar o paciente para salvar no banco de dados
        const pacienteBD = {
            nome: pacienteCadsus.nome,
            nome_mae: pacienteCadsus.nomeMae,
            nome_pai: pacienteCadsus.nomePai,
            cpf: pacienteCadsus.cpf ? await Util.removerMascaraNumero(pacienteCadsus.cpf) : null,
            cartao_sus: pacienteCadsus.cns || pacienteCadsus.cartao_sus,
            id_sexo: id_sexo,
            id_raca: id_raca,
            data_nascimento: pacienteCadsus.dataNascimento ? moment(pacienteCadsus.dataNascimento).format('YYYY-MM-DD') : null,
            id_cidade_nascimento: null,
            id_endereco_paciente: null
        };

        const result = await trx.insert(pacienteBD).into('public.paciente').returning('id').transacting(trx);
        id_paciente = Number.isInteger(result[0]) ? result[0] : result[0].id;

        const idEndereco = await Paciente.salvarEndereco(trx, id_paciente, pacienteCadsus.endereco);

        if (pacienteCadsus.email) {
            const contatoEmail = {
                id_paciente: id_paciente,
                id_tipo_contato: EMAIL,
                contato: pacienteCadsus.email || null
            };
            await Paciente.salvarContato(trx, contatoEmail);
        }
        if (pacienteCadsus.telefone) {
            const contatoTelefone = {
                id_paciente: id_paciente,
                id_tipo_contato: TELEFONE_RESIDENCIAL,
                contato: pacienteCadsus.telefone || null
            };
            await Paciente.salvarContato(trx, contatoTelefone);
        }
        if (pacienteCadsus.celular) {
            const contatoCelular = {
                id_paciente: id_paciente,
                id_tipo_contato: TELEFONE_CELULAR,
                contato: pacienteCadsus.celular || null
            };
            await Paciente.salvarContato(trx, contatoCelular);
        }

        // Relaciona o endereço ao paciente
        await trx('public.paciente').where('id', '=', id_paciente).update({ id_endereco_paciente: idEndereco }).transacting(trx);
        return id_paciente;
    },

    pesquisarPacientesDefinitivos: async (trx, { id_paciente, nome, cpf, cns, cartao_sus }) => {
        // Busca de pacientes definitivos considerando valores nulos
        let whereClauses = [];
        if (id_paciente) {
            whereClauses.push(`p.id = ${id_paciente}`);
        }
        if (nome) {
            whereClauses.push(`p.nome LIKE '${nome}'`);
        }
        if (cpf) {
            whereClauses.push(`p.cpf = ${cpf}`);
        }
        if (cns || cartao_sus) {
            whereClauses.push(`p.cartao_sus = '${cns || cartao_sus}'`);
        }
        // Se nenhum parâmetro foi informado, retorna vazio
        if (whereClauses.length === 0) {
            throw new Error('Nenhum parâmetro de busca informado para pacientes definitivos.');
        }
        const selectQuery = `
            SELECT p.id, p.nome, p.nome_mae, p.nome_pai, p.cpf, p.cartao_sus, p.id_sexo, p.id_raca, p.data_nascimento, p.id_endereco_paciente,
            string_agg(distinct cp.id::text, ', ') id_contato_paciente,
            string_agg(distinct ep.id::text, ', ') as id_endereco_paciente2,
            string_agg(distinct ep2.id::text, ', ') AS id_endereco_paciente3
            FROM public.paciente p
            LEFT JOIN public.contato_paciente cp ON cp.id_paciente = p.id
            LEFT JOIN public.endereco_paciente ep ON ep.id = p.id_endereco_paciente
            LEFT JOIN public.endereco_paciente ep2 ON ep2.id_paciente = p.id
            WHERE ${whereClauses.join(' OR ')}
            GROUP BY p.id;
        `;
        return trx.raw(selectQuery);
    },

    deletarPacientes: async (trx, pacienteEscolhido, idsDefinitivosDeletar, idsProvisoriosDeletar) => {
        /**
         * EXCLUSÃO DOS PACIENTES PROVISÓRIOS
         */
        if (pacienteEscolhido && idsProvisoriosDeletar.length > 0) {
            const idPacienteEscolhido = pacienteEscolhido.id;
            const idsProvisorios = idsProvisoriosDeletar.join(', ');

            // Primeiro, deletar os contatos relacionados aos pacientes provisórios
            const deleteContatoPacienteProvisorioQuery = `
                DELETE FROM public.contato_paciente_provisorio
                WHERE id_paciente_provisorio in (${idsProvisorios});
            `;

            const updateExameAgendadoQuery = `
                UPDATE stt.exame_agendado_micromed
                SET id_paciente = ${idPacienteEscolhido}, id_paciente_provisorio = null
                WHERE id_paciente_provisorio in (${idsProvisorios});
            `;

            const updateExameQuery = `
                UPDATE stt.exame
                SET id_paciente_provisorio = null, id_paciente_definitivo = ${idPacienteEscolhido}
                WHERE id_paciente_provisorio in (${idsProvisorios});
            `;

            // Remover vrelação com paciente definitivo (criada para manter trilha de auditoria (e  por isso não deveria ser removido...))
            const updatePacienteDefinitivoQuery = `
                UPDATE public.paciente
                SET paciente_provisorio_id = NULL
                WHERE paciente_provisorio_id IN (${idsProvisorios});
            `;

            const deletePacienteProvisorioQuery = `
                DELETE FROM public.paciente_provisorio
                WHERE id in (${idsProvisorios});
            `;

            await trx.raw(deleteContatoPacienteProvisorioQuery);
            await trx.raw(updateExameAgendadoQuery);
            await trx.raw(updateExameQuery);
            await trx.raw(updatePacienteDefinitivoQuery);
            await trx.raw(deletePacienteProvisorioQuery);

            console.log('Atualizações e exclusões do paciente provisórios realizadas com sucesso.');
        }

        /**
         * EXCLUSÃO DOS PACIENTES DEFINITIVOS
         */
        if (pacienteEscolhido && idsDefinitivosDeletar.length > 0) {
            const idsDefinitivosExclusao = idsDefinitivosDeletar.join(', ');
            const idEnderecoEscolhido = pacienteEscolhido.id_endereco_paciente;
            const idPacienteEscolhido = pacienteEscolhido.id;

            // Deletar contatos dos pacientes definitivos
            // e Atualizar pacientes definitivos para remover id_endereco_paciente
            const deleteContatoPacienteQuery = `
                    DELETE FROM public.contato_paciente WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updatePacienteQuery = `
                    UPDATE public.paciente SET id_endereco_paciente = null WHERE id in (${idsDefinitivosExclusao});
                `;
            await trx.raw(deleteContatoPacienteQuery);
            await trx.raw(updatePacienteQuery);

            // Atualizar teleconsultoria_solicitacao_paciente
            // Atualizar segunda_opiniao_duvida_paciente
            const updateTeleconsultoriaSolicitacaoPacienteQuery = `
                    UPDATE telessaude.teleconsultoria_solicitacao_paciente
                    SET id_paciente = ${idPacienteEscolhido}, id_endereco_paciente = ${idEnderecoEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updateSegundaOpiniaoDuvidaPacienteQuery = `
                    UPDATE telessaude.segunda_opiniao_duvida_paciente
                    SET id_paciente = ${idPacienteEscolhido}, id_endereco_paciente = ${idEnderecoEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updateTeleconsultoriaCondutaClinicaQuery = `
                    UPDATE telessaude.teleconsultoria_conduta_clinica
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            // Atualizar helpdesk_atendimento_chat
            const updateHelpdeskAtendimentoChatQuery = `
                    UPDATE helpdesk_atendimento_chat
                    SET id_paciente_atendido = ${idPacienteEscolhido}
                    WHERE id_paciente_atendido in (${idsDefinitivosExclusao});
                `;
            await trx.raw(updateTeleconsultoriaSolicitacaoPacienteQuery);
            await trx.raw(updateSegundaOpiniaoDuvidaPacienteQuery);
            await trx.raw(updateTeleconsultoriaCondutaClinicaQuery);
            await trx.raw(updateHelpdeskAtendimentoChatQuery);

            // Atualizar encaminhamento_dermato
            // Atualizar exame
            // Atualizar exame_agendado_micromed
            const updateEncaminhamentoDermatoQuery = `
                    UPDATE stt.encaminhamento_dermato
                    SET id_paciente_definitivo = ${idPacienteEscolhido}
                    WHERE id_paciente_definitivo in (${idsDefinitivosExclusao});
                `;
            const updateExameQuery = `
                    UPDATE stt.exame
                    SET id_paciente_definitivo = ${idPacienteEscolhido}, id_endereco_paciente = ${idEnderecoEscolhido}
                    WHERE id_paciente_definitivo in (${idsDefinitivosExclusao});
                `;
            const updateExameAgendadoMicromedQuery = `
                    UPDATE stt.exame_agendado_micromed
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            await trx.raw(updateEncaminhamentoDermatoQuery);
            await trx.raw(updateExameQuery);
            await trx.raw(updateExameAgendadoMicromedQuery);

            // Atualizar solicitacao_exame_eletro
            // Atualizar solicitacao_exame_eeg
            // Atualizar solicitacao_exame_espiro
            // Atualizar solicitacao_exame_patologia_bucal
            // Deletar endereços dos pacientes definitivos
            const updateSolicitacaoExameEletroQuery = `
                    UPDATE stt.solicitacao_exame_eletro
                    SET id_paciente = ${idPacienteEscolhido}, id_endereco_paciente = ${idEnderecoEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updateSolicitacaoExameEegQuery = `
                    UPDATE stt.solicitacao_exame_eeg
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updateSolicitacaoExameEstomatoQuery = `
                    UPDATE stt.solicitacao_exame_estomato
                    SET id_paciente = ${idPacienteEscolhido}, id_endereco_paciente = ${idEnderecoEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updateSolicitacaoExameEspiroQuery = `
                    UPDATE stt.solicitacao_exame_espiro
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            // Atualizar solicitacao_exame_patologia_bucal
            const updateSolicitacaoExamePatologiaBucalQuery = `
                    UPDATE stt.solicitacao_exame_patologia_bucal
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            // Atualizar apac, tfd
            const updateApacQuery = `
                    UPDATE stt.apac
                    SET id_paciente = ${idPacienteEscolhido}, id_endereco_paciente = ${idEnderecoEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            const updateTfdQuery = `
                    UPDATE stt.tfd
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;

            await trx.raw(updateApacQuery);
            await trx.raw(updateTfdQuery);

            const updateEnderecoPacienteQuery = `
                    UPDATE public.endereco_paciente
                    SET id_paciente = ${idPacienteEscolhido}
                    WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            await trx.raw(updateEnderecoPacienteQuery);

            const deleteEnderecoPacienteQuery = `
                    DELETE FROM public.endereco_paciente WHERE id_paciente in (${idsDefinitivosExclusao});
                `;
            await trx.raw(updateSolicitacaoExameEletroQuery);
            await trx.raw(updateSolicitacaoExameEstomatoQuery);
            await trx.raw(updateSolicitacaoExameEegQuery);
            await trx.raw(updateSolicitacaoExameEspiroQuery);
            await trx.raw(updateSolicitacaoExamePatologiaBucalQuery);
            await trx.raw(deleteEnderecoPacienteQuery);

            // Deletar pacientes definitivos
            const deletePacienteQuery = `
                    DELETE FROM public.paciente WHERE id in (${idsDefinitivosExclusao});
                `;
            await trx.raw(deletePacienteQuery);

            console.log('Atualizações dos pacientes definitivos realizadas com sucesso.');
        }
    },

    _prepararPaciente: async (pacienteCadsus) => {
        return {
            nome: pacienteCadsus.nome,
            cpf: pacienteCadsus.cpf ? await Util.removerMascaraNumero(pacienteCadsus.cpf) : null,
            cartao_sus: pacienteCadsus.cns || pacienteCadsus.cartao_sus,
            data_nascimento: pacienteCadsus.dataNascimento ? moment(pacienteCadsus.dataNascimento).format('YYYY-MM-DD') : null
        };
    },

    pesquisarPacientesProvisorios: async (trx, { nome, cpf, cns, cartao_sus }) => {
        // Busca de pacientes provisórios considerando valores nulos
        let whereClauses = [];
        if (nome) {
            whereClauses.push(`pp.nome LIKE '${nome}'`);
        }
        if (cpf) {
            whereClauses.push(`pp.cpf = ${cpf}`);
        }
        if (cns || cartao_sus) {
            whereClauses.push(`pp.cartao_sus = '${cns || cartao_sus}'`);
        }
        // Se nenhum parâmetro foi informado, retorna vazio
        if (whereClauses.length === 0) {
            throw new Error('Nenhum parâmetro de busca informado para pacientes provisórios.');
        }
        const selectProvisorioQuery = `
            SELECT * FROM public.paciente_provisorio pp
            WHERE ${whereClauses.join(' OR ')}
        `;
        const pacientesProvisoriosResult = await trx.raw(selectProvisorioQuery);
        return pacientesProvisoriosResult;
    },

    pesquisarPacientesDefinitivosSemelhantesComRelatorio: async (trx, pacienteCadsus, filtroCustomizavelCb) => {
        const relatorio = {
            pacienteDefinitivoCriado: false,
            totalPacientesDefinitivos: 0,
            totalPacientesDefinitivosSemelhantes: 0
        };

        // 1 - Extrair do pacienteCadsus os campos { cpf, cns, nome e dataNascimento }. Critérios que serão utilizados numa primeira busca.
        const paciente = await Paciente._prepararPaciente(pacienteCadsus);

        // 2 - Buscar pacientes definitivos, caso não encontre, criar um paciente definitivo.
        let pacientesDefinitivosResult = await Paciente.pesquisarPacientesDefinitivos(trx, paciente);
        relatorio.totalPacientesDefinitivos = pacientesDefinitivosResult.rows.length;
        console.log('Número de pacientes definitivos encontrados:', relatorio.totalPacientesDefinitivos);
        if (pacientesDefinitivosResult.rows.length === 0 && (pacienteCadsus.cpf || pacienteCadsus.cns)) {
            await Paciente.salvarPacienteCadsus(trx, pacienteCadsus);
            relatorio.pacienteDefinitivoCriado = true;
            pacientesDefinitivosResult = await Paciente.pesquisarPacientesDefinitivos(trx, paciente);
        } else if (pacientesDefinitivosResult.rows.length === 0) {
            throw new Error('Nenhum paciente definitivo encontrado!');
        }

        // 3 - Pacientes definitivos são filtrados com base sempre em dois atributos (critério 1, 2, 3, 4, 5). Podem definir seu proprio critério através do filtroCustomizavelCb. Esses pacientes escolhidos serão unificados.
        let pacientesDefinitivos = [];
        if (filtroCustomizavelCb && typeof filtroCustomizavelCb === 'function') {
            pacientesDefinitivos = await filtroCustomizavelCb(pacientesDefinitivosResult);
        } else {
            pacientesDefinitivos = pacientesDefinitivosResult.rows?.filter((p) => {
                const dataNascimentoFormatada = moment(p.data_nascimento).format('YYYY-MM-DD');
                const cpfPaciente = paciente.cpf ? paciente.cpf.toString().padStart(11, '0') : null;
                const cpfDefinitivo = p.cpf ? p.cpf.toString().padStart(11, '0') : null;

                const nomeSimilar = Util.verificarSimilaridade(p.nome, paciente.nome);
                const nomeIgual = Util.igualdadeEntreNomes(p.nome, paciente.nome);
                const cpfIgual = (cpfDefinitivo === cpfPaciente) && cpfPaciente !== null;
                const cartaoSusIgual = (p.cartao_sus === paciente.cartao_sus) && paciente.cartao_sus !== null;
                const dtNascimentoIgual = (dataNascimentoFormatada === paciente.data_nascimento) && paciente.data_nascimento !== null;

                // O primeiro critério só é usado se o nome tiver mais de dois termos.
                const tokensNome = paciente.nome ? paciente.nome.split(' ') : [];
                const criterio01 = tokensNome.length > 2 ? nomeIgual && dtNascimentoIgual : false;
                const criterio02 = nomeSimilar && cpfIgual;
                const criterio03 = nomeSimilar && cartaoSusIgual;
                const criterio04 = cpfIgual && dtNascimentoIgual;
                const criterio05 = cartaoSusIgual && dtNascimentoIgual;
                return criterio01 || criterio02 || criterio03 || criterio04 || criterio05;
            });
        }
        relatorio.totalPacientesDefinitivosSemelhantes = pacientesDefinitivos.length;
        console.log('Número de pacientes definitivos semelhantes encontrados:', relatorio.totalPacientesDefinitivosSemelhantes);

        // 4 - Pode acontecer dos dados não estarem em nenhum critério de escolha, e portanto deve-se salvar o paciente Cadsus com seus dados completos.
        if (pacientesDefinitivos.length === 0) {
            const id_paciente = await Paciente.salvarPacienteCadsus(trx, pacienteCadsus);
            relatorio.pacienteDefinitivoCriado = true;
            pacientesDefinitivosResult = await Paciente.pesquisarPacientesDefinitivos(trx, { id_paciente });
            pacientesDefinitivos = pacientesDefinitivosResult.rows;
        }

        return [pacientesDefinitivos, relatorio];
    },

    /**
     * Utilizado em três lugares diferentes:
     * - stt-dataflow, unificação manual dos paacientes
     * - stt-oauth2, assim que entrar por gov.br ou login do paciente.
     * - stt-administrativo, no botão de consolidação de pacientes.
     *
     * @param {*} trx
     * @param {*} pacienteCadsus
     * @param {*} filtroCustomizavelCb
     * @returns
     */
    pesquisarPacientesDefinitivosSemelhantes: async (trx, pacienteCadsus, filtroCustomizavelCb) => {
        const [pacientesDefinitivos, _] = await Paciente.pesquisarPacientesDefinitivosSemelhantesComRelatorio(trx, pacienteCadsus, filtroCustomizavelCb);
        return pacientesDefinitivos;
    },

    pesquisarPacientesProvisoriosSemelhantesComRelatorio: async (trx, pacienteCadsus, filtroCustomizavelCb) => {
        const relatorio = {
            totalPacientesProvisorios: 0,
            totalPacientesProvisoriosSemelhantes: 0
        };

        // 1 - Extrair do pacienteCadsus os campos { cpf, cns, nome e dataNascimento }. Critérios que serão utilizados numa primeira busca.
        const paciente = await Paciente._prepararPaciente(pacienteCadsus);

        // 5 - Buscar todos pacientes provisórios, para que posteriormente sejam escolhidos pelos critérios.
        const pacientesProvisoriosResult = await Paciente.pesquisarPacientesProvisorios(trx, paciente);
        relatorio.totalPacientesProvisorios = pacientesProvisoriosResult.rows.length;
        console.log('Número de pacientes provisórios encontrados:', relatorio.totalPacientesProvisorios);

        // 6 - Escolher pacientes provisórios com base em dois atributos (critérios 1, 2 e 3). Esses pacientes provisórios serão unificados.
        let pacientesProvisorios;
        if (filtroCustomizavelCb && typeof filtroCustomizavelCb === 'function') {
            pacientesProvisorios = await filtroCustomizavelCb(pacientesProvisoriosResult);
        } else {
            pacientesProvisorios = (pacientesProvisoriosResult.rows || []).filter((p) => {
                const dataNascimentoFormatada = moment(p.data_nascimento).format('YYYY-MM-DD');
                const cpfPaciente = paciente.cpf ? paciente.cpf.toString().padStart(11, '0') : null;
                const cpfDefinitivo = p.cpf ? p.cpf.toString().padStart(11, '0') : null;

                const nomeSimilar = Util.verificarSimilaridade(p.nome, paciente.nome);
                const nomeIgual = Util.igualdadeEntreNomes(p.nome, paciente.nome);
                const cpfIgual = (cpfDefinitivo === cpfPaciente) && cpfPaciente !== null;
                const cartaoSusIgual = (p.cartao_sus === paciente.cartao_sus) && paciente.cartao_sus !== null;
                const dtNascimentoIgual = (dataNascimentoFormatada === paciente.data_nascimento) && paciente.data_nascimento !== null;

                // O primeiro critério só é usado se o nome tiver mais de dois termos.
                const tokensNome = paciente.nome ? paciente.nome.split(' ') : [];
                const criterio01 = tokensNome.length > 2 ? nomeIgual && dtNascimentoIgual : false;
                const criterio02 = nomeSimilar && cpfIgual;
                const criterio03 = nomeSimilar && cartaoSusIgual;
                const criterio04 = cpfIgual && dtNascimentoIgual;
                const criterio05 = cartaoSusIgual && dtNascimentoIgual;
                return criterio01 || criterio02 || criterio03 || criterio04 || criterio05;
            });
        }
        relatorio.totalPacientesProvisoriosSemelhantes = pacientesProvisorios.length;
        console.log('Número de pacientes provisórios semelhantes encontrados:', relatorio.totalPacientesProvisoriosSemelhantes);

        return [pacientesProvisorios, relatorio];
    },

    pesquisarPacientesProvisoriosSemelhantes: async (trx, pacienteCadsus, filtroCustomizavelCb) => {
        const [pacientesProvisorios, _] = await Paciente.pesquisarPacientesProvisoriosSemelhantesComRelatorio(trx, pacienteCadsus, filtroCustomizavelCb);
        return pacientesProvisorios;
    },

    /**
     *
     * @param {*} trx, transação do banco de dados
     * @param {*} pacienteCadsus, utilizar o retorno da função Servicos.PacienteCadsus.buscar({ cpf: payload.cpf }, global.gConfig);
     * @param {*} options => { retornarRelatorio: boolean }
     * @returns
     */
    unificar: async (trx, pacienteCadsus, options = {}) => {
        try {
            // 1 - Extrair do pacienteCadsus os campos { cpf, cns, nome e dataNascimento }. Critérios que serão utilizados numa primeira busca.
            // 2 - Buscar pacientes definitivos, caso não encontre, criar um paciente definitivo.
            // 3 - Pacientes definitivos são filtrados com base sempre em dois atributos (critério 1, 2, 3, 4, 5). Podem definir seu proprio critério através do filtroCustomizavelCb. Esses pacientes escolhidos serão unificados.
            // 4 - Pode acontecer dos dados não estarem em nenhum critério de escolha, e portanto deve-se salvar o paciente Cadsus com seus dados completos.
            const [pacientesDefinitivos, relatorioDefinitivos] = await Paciente.pesquisarPacientesDefinitivosSemelhantesComRelatorio(trx, pacienteCadsus);
            // 5 - Buscar todos pacientes provisórios, para que posteriormente sejam escolhidos pelos critérios.
            // 6 - Escolher pacientes provisórios com base em dois atributos (critérios 1, 2 e 3). Esses pacientes provisórios serão unificados.
            const [pacientesProvisorios, relatorioProvisorios] = await Paciente.pesquisarPacientesProvisoriosSemelhantesComRelatorio(trx, pacienteCadsus);

            // 7 - Escolher o paciente definitivo que possui mais atributos preenchidos.
            let pacientesFiltrados = [...pacientesDefinitivos];
            for (const criterio of criteriosEscolhaPaciente) {
                const pacientesFiltradosPorCriterio = pacientesFiltrados.filter((p) => p[criterio] !== null && p[criterio] !== '');
                if (pacientesFiltradosPorCriterio.length === 1) {
                    pacientesFiltrados = pacientesFiltradosPorCriterio;
                    console.log(`Paciente escolhido pelo critério ${criterio}. Os demais não possuem esse critério.`);
                    break;
                } else if (pacientesFiltradosPorCriterio.length > 1) {
                    pacientesFiltrados = [...pacientesFiltradosPorCriterio];
                }
            }
            let pacienteEscolhido = null;
            if (pacientesFiltrados.length > 0) {
                pacienteEscolhido = pacientesFiltrados[0];
                console.log('Paciente escolhido:', pacienteEscolhido);
            } else {
                console.log('Nenhum paciente único encontrado com os critérios fornecidos.');
                throw new Error('Nenhum paciente definitivo encontrado!');
            }

            if (!pacienteEscolhido) {
                console.log('Nenhum paciente encontrado com os critérios fornecidos.');
            }

            // Para o relatorio final
            const countExamesQuery = `
                SELECT count(distinct e.id) as total_exames
                FROM stt.exame e
                WHERE id_paciente_definitivo = ${pacienteEscolhido.id};
            `;
            const countExamesResult = await trx.raw(countExamesQuery);
            const totalExames = countExamesResult.rows[0].total_exames;
            console.log(`O paciente escolhido possui ${totalExames} exames.`);

            /**
             * 8 - EXCLUSÃO DOS PACIENTES PROVISÓRIOS E DEFINITIVOS
             */
            let idsProvisoriosDeletar = pacientesProvisorios.map((p) => p.id);
            let idsDefinitivosDeletar = pacientesDefinitivos.map((p) => p.id).filter((id) => id !== pacienteEscolhido.id);
            await Paciente.deletarPacientes(trx, pacienteEscolhido, idsDefinitivosDeletar, idsProvisoriosDeletar);

            /**
             * 9 - RELATÓRIOS E LOGS do processo de unificação.
             */
            const nomePacienteEscolhido = pacienteEscolhido.nome.toUpperCase();
            const nomePacienteCadsus = pacienteCadsus.nome.toUpperCase();
            const nomesSaoSimilares = Util.verificarSimilaridade(nomePacienteEscolhido, nomePacienteCadsus);
            if (!nomesSaoSimilares) {
                console.log('Os nomes não possuem similaridade de 80% ou mais.');
                throw new Error('Os nomes não possuem similaridade de 80% ou mais.');
            }

            console.log('Nome paciente escolhido:', nomePacienteEscolhido);
            console.log('Nome paciente cadsus:', nomePacienteCadsus);
            console.log('Os nomes possuem similaridade de 80% ou mais:', nomesSaoSimilares);

            // Atualizar paciente escolhido
            console.log('Atualizando paciente escolhido com os dados do cadsus');
            const updateFields = [];
            if (pacienteCadsus.nome) updateFields.push(`nome = '${pacienteCadsus.nome.toUpperCase()}'`);
            if (pacienteCadsus.cpf) updateFields.push(`cpf = '${pacienteCadsus.cpf}'`);
            if (pacienteCadsus.cns) updateFields.push(`cartao_sus = '${pacienteCadsus.cns}'`);
            if (pacienteCadsus.nomeMae && pacienteCadsus.nomeMae.trim() !== '') {
                updateFields.push(`nome_mae = '${pacienteCadsus.nomeMae.trim().toUpperCase()}'`);
            }
            if (pacienteCadsus.dataNascimento) {
                updateFields.push(`data_nascimento = '${moment(pacienteCadsus.dataNascimento).format('YYYY-MM-DD')}'`);
            }
            if (pacienteCadsus.nomePai && pacienteCadsus.nomePai.trim() !== '') {
                updateFields.push(`nome_pai = '${pacienteCadsus.nomePai.trim().toUpperCase()}'`);
            }
            const updatePacienteEscolhidoQuery = `
                UPDATE public.paciente
                SET ${updateFields.join(', ')}
                WHERE id = ${pacienteEscolhido.id};
            `;
            await trx.raw(updatePacienteEscolhidoQuery);

            // Relatório final das unificações
            const countExamesAposQuery = `
                SELECT count(distinct e.id) as total_exames
                FROM stt.exame e
                WHERE id_paciente_definitivo = ${pacienteEscolhido.id};
            `;
            const countExamesAposResult = await trx.raw(countExamesAposQuery);
            const totalExamesApos = countExamesAposResult.rows[0].total_exames;
            console.log(`Número total de exames antes da unificação: ${totalExames}`);
            console.log(`Número total de exames após unificação: ${totalExamesApos}`);

            const pacientesDefinitivosApos = await Paciente.pesquisarPacientesDefinitivos(trx, { nome: pacienteEscolhido.nome, cpf: pacienteEscolhido.cpf, cartao_sus: pacienteEscolhido.cartao_sus });
            console.log('Número total de pacientes definitivos antes da unificação:', pacientesDefinitivos.length);
            console.log('Número total de pacientes definitivos após a unificação:', pacientesDefinitivosApos.rows.length);

            const pacientesProvisoriosApos = await Paciente.pesquisarPacientesProvisorios(trx, { nome: pacienteEscolhido.nome, cpf: pacienteEscolhido.cpf, cartao_sus: pacienteEscolhido.cartao_sus });
            console.log('Número total de pacientes provisórios antes da unificação:', pacientesProvisorios.length);
            console.log('Número total de pacientes provisórios após a unificação:', pacientesProvisoriosApos.rows.length);

            // Exibir os demais provisorios em forma de tabela pacientesProvisorios
            console.log('Pacientes provisórios encontrados:');
            for (const pacienteProvisorio of pacientesProvisorios) {
                console.log(`${pacienteProvisorio.nome} | ${pacienteProvisorio.cpf} | ${pacienteProvisorio.cartao_sus} | ${pacienteProvisorio.data_nascimento} | ${pacienteProvisorio.nome_mae}`);
            }

            // Exibir os demais definitivos em forma de tabela pacientesDefinitivos
            console.log('Pacientes definitivos encontrados:');
            for (const pacienteDefinitivo of pacientesDefinitivos) {
                console.log(`${pacienteDefinitivo.nome} | ${pacienteDefinitivo.cpf} | ${pacienteDefinitivo.cartao_sus} | ${pacienteDefinitivo.data_nascimento} | ${pacienteDefinitivo.nome_mae}`);
            }
            // Resultado final do paciente escolhido
            const selectPacienteEscolhidoQuery = `
                SELECT * FROM public.paciente
                WHERE id = ${pacienteEscolhido.id};
            `;
            const pacienteEscolhidoFinal = (await trx.raw(selectPacienteEscolhidoQuery)).rows[0];

            if (options?.retornarRelatorio) {
                return {
                    ...relatorioDefinitivos,
                    ...relatorioProvisorios,
                    totalPacientesAposSemCriterio: pacientesDefinitivosApos.rows?.length + pacientesProvisoriosApos.rows?.length,
                    totalPacientesDefinitivosExcluidos: idsDefinitivosDeletar.length,
                    totalPacientesProvisoriosExcluidos: idsProvisoriosDeletar.length,
                    totalPacientesExcluidos: idsDefinitivosDeletar.length + idsProvisoriosDeletar.length
                };
            }
            return pacienteEscolhidoFinal;
        } catch (error) {
            console.error('❌ Erro na unificação:', error.message);
            throw new Error(`Erro na unificação do paciente: ${error.message}`);
        }
    }
};

module.exports = Paciente;
